import { useCallback, useEffect, useState } from 'react';
import { geospatialApi } from '../../../api/geospatialApi';
import { getCurrentRoleId, getCurrentUserScope } from '../../../api/viewerAccess';
import { useCollectionSync } from '../../../hooks/useCollectionSync';

function toOption(item) {
  if (item === null || item === undefined) return null;

  if (typeof item === 'string' || typeof item === 'number') {
    const text = String(item).trim();
    return text ? { value: text, label: text } : null;
  }

  const value =
    item.value ??
    item.code ??
    item.id ??
    item.uuid ??
    item.state ??
    item.lga ??
    item.ward ??
    item.name ??
    item.label ??
    '';

  const textValue = String(value).trim();
  if (!textValue) return null;

  const label = item.label ?? item.name ?? item.title ?? textValue;
  return {
    value: textValue,
    label: String(label).trim() || textValue,
  };
}

function normalizeOptions(items, fallback = []) {
  const deduped = new Map();

  [...fallback, ...(Array.isArray(items) ? items : [])]
    .map(toOption)
    .filter(Boolean)
    .forEach((option) => {
      if (!deduped.has(option.value)) {
        deduped.set(option.value, option);
      }
    });

  return Array.from(deduped.values()).sort((a, b) => a.label.localeCompare(b.label));
}

// Narrows options to the user's assigned value. Matches case-insensitively
// and always returns that value as the option value, so a form holding the
// scope value shows it selected; falls back to the scope value itself when
// the geo list doesn't contain it.
function scopedOptions(options, scopeValue) {
  if (!scopeValue) return options;
  const key = scopeValue.toLowerCase();
  const match = options.find((o) => o.value.toLowerCase() === key || o.label.toLowerCase() === key);
  return [{ value: scopeValue, label: match?.label ?? scopeValue }];
}

function extractItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  if (Array.isArray(payload?.data?.results)) return payload.data.results;
  return [];
}

// Geographic scope by role (backend: one assigned state per user):
//   R01 / national roles — every state, LGA and ward.
//   R04 State Officer   — locked to the assigned state; any LGA / ward in it.
//   R05 Field Officer   — locked to the assigned state + LGA; any ward in it.
export function useScopeLock() {
  const roleId = getCurrentRoleId();
  const scope = getCurrentUserScope(roleId);
  return {
    roleId,
    lockedState: ['R04', 'R05'].includes(roleId) ? String(scope.state ?? '').trim() : '',
    lockedLga: roleId === 'R05' ? String(scope.lga ?? '').trim() : '',
  };
}

// Keeps a form's state / LGA fields on the user's locked values (R04, R05),
// so those dropdowns can be shown pre-filled and disabled. Pass
// `lgaKey: null` for a form that has no LGA field.
export function useApplyScopeLock(values, setValues, { stateKey = 'state', lgaKey = 'lga', enabled = true } = {}) {
  const lock = useScopeLock();
  const { lockedState, lockedLga } = lock;
  const currentState = values?.[stateKey];
  const currentLga = lgaKey ? values?.[lgaKey] : undefined;
  useEffect(() => {
    if (!enabled || !lockedState) return;
    const lgaLocked = Boolean(lgaKey && lockedLga);
    if (currentState === lockedState && (!lgaLocked || currentLga === lockedLga)) return;
    setValues((prev) => {
      const next = { ...prev, [stateKey]: lockedState };
      if (lgaKey) {
        if (lgaLocked) next[lgaKey] = lockedLga;
        else if (prev?.[stateKey] !== lockedState) next[lgaKey] = '';
      }
      return next;
    });
  }, [enabled, lockedState, lockedLga, currentState, currentLga, stateKey, lgaKey, setValues]);
  return lock;
}

// `unscoped: true` lists every state even for R04 / R05 — for fields that
// are not the record's own location (e.g. a route's destination state).
export function useLocationOptions(state = '', lga = '', { unscoped = false } = {}) {
  const [stateOptions, setStateOptions] = useState([]);
  const [lgaOptions, setLgaOptions] = useState([]);
  const [wardOptions, setWardOptions] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [loadingWards, setLoadingWards] = useState(false);
  const roleId = getCurrentRoleId();
  const scope = getCurrentUserScope(roleId);
  const scopeState = !unscoped && ['R04', 'R05'].includes(roleId) ? String(scope.state ?? '').trim() : '';
  const scopeLga = !unscoped && roleId === 'R05' ? String(scope.lga ?? '').trim() : '';
  const resolvedState = scopeState || String(state ?? '').trim();
  const resolvedLga = scopeLga || String(lga ?? '').trim();

  const loadStates = useCallback(() => {
    let active = true;
    setLoadingStates(true);

    geospatialApi.states()
      .then((payload) => {
        if (!active) return;
        const options = normalizeOptions(extractItems(payload));
        setStateOptions(scopedOptions(options, scopeState));
      })
      .catch(() => {
        if (!active) return;
        setStateOptions(scopedOptions([], scopeState));
      })
      .finally(() => {
        if (active) setLoadingStates(false);
      });

    return () => {
      active = false;
    };
  }, [scopeState]);

  const loadLgas = useCallback((stateValue = resolvedState) => {
    let active = true;
    const stateToLoad = scopeState || String(stateValue ?? '').trim();

    if (!stateToLoad) {
      setLgaOptions([]);
      setLoadingLgas(false);
      return () => {
        active = false;
      };
    }

    setLgaOptions([]);
    setWardOptions([]);
    setLoadingLgas(true);
    geospatialApi.lgas(stateToLoad)
      .then((payload) => {
        if (!active) return;
        const options = normalizeOptions(extractItems(payload));
        setLgaOptions(scopedOptions(options, scopeLga));
      })
      .catch(() => {
        if (!active) return;
        setLgaOptions(scopedOptions([], scopeLga));
      })
      .finally(() => {
        if (active) setLoadingLgas(false);
      });

    return () => {
      active = false;
    };
  }, [resolvedState, scopeState, scopeLga]);

  const loadWards = useCallback((stateValue = resolvedState, lgaValue = resolvedLga) => {
    let active = true;
    const stateToLoad = scopeState || String(stateValue ?? '').trim();
    const lgaToLoad = scopeLga || String(lgaValue ?? '').trim();

    if (!stateToLoad || !lgaToLoad) {
      setWardOptions([]);
      setLoadingWards(false);
      return () => {
        active = false;
      };
    }

    setWardOptions([]);
    setLoadingWards(true);
    geospatialApi.wards(stateToLoad, lgaToLoad)
      .then((payload) => {
        if (!active) return;
        setWardOptions(normalizeOptions(extractItems(payload)));
      })
      .catch(() => {
        if (!active) return;
        setWardOptions([]);
      })
      .finally(() => {
        if (active) setLoadingWards(false);
      });

    return () => {
      active = false;
    };
  }, [resolvedState, resolvedLga, scopeState, scopeLga]);

  useEffect(() => {
    const cancel = loadStates();
    return () => {
      if (typeof cancel === 'function') cancel();
    };
  }, [loadStates]);

  useEffect(() => {
    const cancel = loadLgas(resolvedState);
    return () => {
      if (typeof cancel === 'function') cancel();
    };
  }, [resolvedState, loadLgas]);

  useEffect(() => {
    const cancel = loadWards(resolvedState, resolvedLga);
    return () => {
      if (typeof cancel === 'function') cancel();
    };
  }, [resolvedState, resolvedLga, loadWards]);

  useCollectionSync(geospatialApi.collectionKey, () => {
    loadStates();
    if (resolvedState) loadLgas(resolvedState);
    if (resolvedState && resolvedLga) loadWards(resolvedState, resolvedLga);
  });

  return {
    stateOptions,
    lgaOptions,
    wardOptions,
    loadingStates,
    loadingLgas,
    loadingWards,
  };
}
